/** The navigator's context and `useOpenIn` (1.I-2 deliverable 3), apart from the provider so that a pane's chunk imports no more than it needs. */
import type { DocumentV1 } from '@codex/contracts';
import { createContext, useContext, useMemo } from 'react';
import type { NavSource } from './decide-open.js';
import type { NavState, OpenIn, OpenRequest, PaneName } from './nav-state.js';

/** Plans and applies one open; null while the shell flag is off. */
export type NavOpen = (request: OpenRequest) => void;

export const NavContext = createContext<NavOpen | null>(null);

/** An `openDoc` for editors that compute their own mode, bound to the pane they sit in; null while `shell` is off. */
export function useOpenIn(source: NavSource): OpenIn | null {
  const open = useContext(NavContext);
  return useMemo(
    () => (open ? (target, mode) => open({ target, mode, source }) : null),
    [open, source],
  );
}

/** The state and its actions, for the content slot and the palette (null while `shell` is off). */
export interface NavApi {
  readonly state: NavState;
  readonly open: NavOpen;
  readonly update: (change: (state: NavState) => NavState) => void;
}

export const NavApiContext = createContext<NavApi | null>(null);

export interface PaneControls {
  readonly onClose: () => void;
  readonly onBack: () => void;
  readonly onForward: () => void;
  readonly onOpenAsPage: () => void;
  readonly onOpenInSplit: () => void;
}

export function useNavApi(): NavApi | null {
  return useContext(NavApiContext);
}

/** The `docs.get` answer `open()` prefetched for a pane's document, while fresh; null otherwise. */
export type PeekAnswers = (docId: string) => Promise<DocumentV1> | null;

export const PeekAnswerContext = createContext<PeekAnswers>(() => null);

export type { PaneName };
